import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDebounce } from "hooks/useDebounce";
import { CoreFilterOptions as FilterOptions, SortOptions } from "./types";
import { SearchSortBar } from ".";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { CategoryType } from "@switcherstudio/switcher-api-client";
import { exists } from "helpers/booleans";
import { useTranslation } from "react-i18next";
import { useClaimCheck } from "hooks/useClaimCheck";
import {
    BroadcastResponse,
    SortType,
    VideoFilterType
} from "@switcherstudio/api-core-client";
import { SearchSortOptions } from "components/generic-multiselect/types";

export interface BroadcastResponsesSearchSortBarProps {
    broadcasts: BroadcastResponse[];
    handleSort: (broadcasts: BroadcastResponse[]) => void;
    searchSortOptions: SearchSortOptions;
}

export const BroadcastResponsesSearchSortBar = ({
    broadcasts,
    handleSort,
    searchSortOptions
}: BroadcastResponsesSearchSortBarProps) => {
    const { location, defaultSortOption, hiddenSortOptions } =
        searchSortOptions;

    const { t } = useTranslation("broadcasts-multi-select");

    const [selectedSortOption, setSelectedSortOption] =
        useState<SortOptions>(defaultSortOption);

    const [selectedCategoryOption, setSelectedCategoryOption] =
        useState<string>("all");

    const [selectedFilterOption, setSelectedFilterOption] = useState<string>(
        FilterOptions.All
    );

    const [query, setQuery] = useState("");
    const { value: debouncedQuery } = useDebounce(query, 500);

    const mapOptionToFilter = (option: string) => {
        const optionFilterMap: Record<FilterOptions, VideoFilterType> = {
            [FilterOptions.All]: "All",
            [FilterOptions.Published]: "Published",
            [FilterOptions.Unpublished]: "Unpublished",
            [FilterOptions.InPlayer]: "InPlayer",
            [FilterOptions.NotInPlayer]: "NotInPlayer",
            [FilterOptions.Shoppable]: "IsShoppable",
            [FilterOptions.NotShoppable]: "NotShoppable",
            [FilterOptions.Gated]: "IsGated",
            [FilterOptions.NotGated]: "NotGated"
        };

        return optionFilterMap[option as FilterOptions] || "All";
    };

    const mapOptionToSortParams = (
        option: string
    ): {
        sortType: SortType;
        sortOrder: "Asc" | "Desc";
    } => {
        switch (option) {
            case SortOptions.NewestFirst:
                return { sortType: "Date", sortOrder: "Desc" };
            case SortOptions.OldestFirst:
                return { sortType: "Date", sortOrder: "Asc" };
            case SortOptions.Next:
                return { sortType: "Date", sortOrder: "Asc" };
            case SortOptions.Last:
                return { sortType: "Date", sortOrder: "Desc" };
            case SortOptions.TitleAZ:
                return { sortType: "Alpha", sortOrder: "Asc" };
            case SortOptions.TitleZA:
                return { sortType: "Alpha", sortOrder: "Desc" };
            case SortOptions.MostViews:
                return { sortType: "Views", sortOrder: "Desc" };
            case SortOptions.LeastViews:
                return { sortType: "Views", sortOrder: "Asc" };
            default:
                return { sortType: "Date", sortOrder: "Desc" };
        }
    };

    const handleSortChange = useCallback(
        (sortOption: string) => {
            if (sortOption) setSelectedSortOption(sortOption as SortOptions);

            if (searchSortOptions?.refetchWithNewParams) {
                const { sortType, sortOrder } =
                    mapOptionToSortParams(sortOption);
                searchSortOptions.refetchWithNewParams({
                    sortType,
                    sortOrder
                });
            }
        },
        [setSelectedSortOption, searchSortOptions]
    );

    const handleCategoryChange = useCallback(
        (category: string) => {
            setSelectedCategoryOption(category);
            if (searchSortOptions?.refetchWithNewParams) {
                searchSortOptions.refetchWithNewParams({
                    tags: category === "all" ? [] : [category]
                });
            }
        },
        [setSelectedCategoryOption, searchSortOptions]
    );

    const handleSelectFilterOption = useCallback(
        (option: string) => {
            setSelectedFilterOption(option);

            if (searchSortOptions?.refetchWithNewParams) {
                const filter = mapOptionToFilter(option);
                searchSortOptions.refetchWithNewParams({ filter });
            }
        },
        [setSelectedFilterOption, searchSortOptions]
    );

    const handleTextChange = useCallback(
        (text: string) => {
            setQuery(text);
        },
        [setQuery]
    );

    const userInfo = useSelector((s: RootState) => s.user?.userInfo);
    const hasCatalogClaim = useClaimCheck("catalog");

    const { data: userCategories, dispatchApiRequest: categoriesRequest } =
        useSwitcherClient((client) => client.categories_GetCategories, {
            suppressErrorBoundary: true,
            requestImmediately: true,
            args: [userInfo?.MetaData?.Details?.ProjectId, CategoryType._0]
        });

    const categoryOptions = useMemo(() => {
        const all = {
            value: "all",
            text: hasCatalogClaim
                ? t("broadcasts-multi-select:all-tags")
                : t("broadcasts-multi-select:all-categories")
        };
        return !exists(userCategories)
            ? [all]
            : [
                  all,
                  ...userCategories?.Categories?.map((category) => {
                      return { value: category.Id, text: category.Name };
                  })
              ];
    }, [t, userCategories, hasCatalogClaim]);

    useEffect(() => {
        categoriesRequest();
        handleSort(broadcasts);

        // Do not include handleSort in dependencies to prevent infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcasts]);

    useEffect(() => {
        // Handle search query change (must be in useEffect to accommodate debounce)
        if (searchSortOptions?.refetchWithNewParams) {
            searchSortOptions.refetchWithNewParams(
                { text: debouncedQuery },
                true
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    return (
        <SearchSortBar
            categoryOptions={categoryOptions}
            handleCategoryChange={handleCategoryChange}
            selectedCategoryOption={selectedCategoryOption}
            handleSortChange={handleSortChange}
            selectedSortOption={selectedSortOption}
            handleTextChange={handleTextChange}
            query={query}
            showFilters={location === "video-library"}
            selectedFilterOption={selectedFilterOption}
            setSelectedFilterOption={handleSelectFilterOption}
            onClear={() => {
                handleTextChange("");
                searchSortOptions?.refetchWithNewParams({ text: "" });
            }}
            hiddenSortOptions={hiddenSortOptions}
            useCoreFilterOptions={true}
        />
    );
};
